import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useEffect } from 'react';
import { useDebounce, useLatestRef, useSafeLayoutEffect } from '@circlefin/hooks';
import { useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel, getPaginationRowModel } from '@tanstack/react-table';
export { createColumnHelper } from '@tanstack/react-table';
import classNames from 'classnames';
import useIsMounted from 'ismounted';
import { Table } from '../Table/Table.es.js';
import { AdvancedTableBody } from './AdvancedTableBody/AdvancedTableBody.es.js';
import { AdvancedTableHead } from './AdvancedTableHead/AdvancedTableHead.es.js';
import { AdvancedTablePages } from './AdvancedTablePages/AdvancedTablePages.es.js';
import { usePagination } from './usePagination/usePagination.es.js';
import { useSelection } from './useSelection/useSelection.es.js';

const EMPTY_OBJ = {};
const AdvancedTable = ({ locale = {
    prevLabel: 'Previous',
    nextLabel: 'Next'
}, pagination = 'default', paginationLinks, pageSize: userPageSize = 10, filters: userFilters = EMPTY_OBJ, sortBy: userSortBy = EMPTY_OBJ, onFetchData = ()=>undefined, className, loading = false, columns, data, initialState, onRowClick, applyGrouping, customTableBodyProps, getRowSubComponent, density, groupHeaderVariant, hoverHighlight = true, layout, selectionType = 'all', showHeader = true, enableRowSelection = false, enableMultiRowSelection = true, onSelectedRowsChange, overflow = false, stickyHeader = false, role, ...props })=>{
    const modifiedColumns = useSelection({
        columns,
        enableRowSelection,
        enableMultiRowSelection,
        selectionType
    });
    // Create table instance
    const table = useReactTable({
        ...props,
        columns: modifiedColumns,
        data,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        enableRowSelection,
        enableMultiRowSelection,
        initialState: {
            ...initialState,
            pagination: {
                ...initialState?.pagination,
                pageSize: userPageSize
            }
        }
    });
    const { currentPageLink, ...paginationControls } = usePagination({
        pagination,
        paginationLinks,
        ...table
    });
    // Get state props for debounce
    const { pagination: { pageIndex, pageSize }, columnFilters, sorting } = table.getState();
    const { setSorting, setColumnFilters, getSelectedRowModel } = table;
    const selectedRows = getSelectedRowModel().flatRows;
    // Debounce our onFetchData call for 100ms
    const onFetchDataDebounced = useDebounce({
        callback: onFetchData,
        delay: 100
    });
    const onLatestSelectedRows = useLatestRef(onSelectedRowsChange);
    const isMounted = useIsMounted();
    // When the table states changes, fetch new data!
    useEffect(()=>{
        if (!isMounted.current) return;
        // Every change will call our debounced function
        onFetchDataDebounced({
            pageIndex,
            pageSize,
            currentPageLink,
            filters: columnFilters.reduce((acc, f)=>({
                    ...acc,
                    [f.id]: f.value
                }), {}),
            sortBy: sorting.reduce((acc, s)=>({
                    ...acc,
                    [s.id]: s.desc
                }), {})
        });
    // Only the last call after the 100ms debounce is over will be fired!
    }, [
        isMounted,
        pageIndex,
        pageSize,
        columnFilters,
        sorting,
        onFetchDataDebounced,
        currentPageLink
    ]);
    /**
     * The checkbox intermediate state is controlled by dom updates via a ref. (Look at checkbox component implementation)
     * When selection state is managed in a controlled manner, this onChange callback that is called within this effect will trigger the Checkbox's intermediate state to be updated via dom update.
     * We are using useSafeLayoutEffect instead of useEffect here because this onChange callback could cause the user to see a flicker when the DOM mutations takes effect.
     * We want this effect to run after the DOM has been updated, but before the browser has had a chance to "paint" those changes (the user doesn't actually see the updates until after the browser has repainted.
     * @see https://kentcdodds.com/blog/useeffect-vs-uselayouteffect#one-special-case
     */ useSafeLayoutEffect(()=>{
        onLatestSelectedRows.current?.(selectedRows);
    }, [
        selectedRows,
        onLatestSelectedRows
    ]);
    useEffect(()=>{
        const updated = [];
        for (const [key, value] of Object.entries(userFilters)){
            updated.push({
                id: key,
                value
            });
        }
        setColumnFilters(updated);
    }, [
        setColumnFilters,
        userFilters
    ]);
    useEffect(()=>{
        const updated = [];
        for (const [key, value] of Object.entries(userSortBy)){
            updated.push({
                id: key,
                desc: value
            });
        }
        setSorting(updated);
    }, [
        setSorting,
        userSortBy
    ]);
    return jsxs(Fragment, {
        children: [
            jsx("div", {
                className: classNames({
                    'overflow-x-auto': overflow
                }),
                children: jsxs(Table, {
                    className: classNames('cb-advanced-table', className),
                    density: density,
                    groupHeaderVariant: groupHeaderVariant,
                    hoverHighlight: hoverHighlight,
                    layout: layout,
                    ...props.getRowCanExpand ? {
                        role: 'treegrid'
                    } : {},
                    children: [
                        jsx(AdvancedTableHead, {
                            ...table,
                            showHeader: showHeader,
                            sticky: stickyHeader
                        }),
                        jsx(AdvancedTableBody, {
                            ...table,
                            applyGrouping: applyGrouping,
                            customTableBodyProps: customTableBodyProps,
                            getRowSubComponent: getRowSubComponent,
                            loading: loading,
                            onRowClick: onRowClick
                        })
                    ]
                })
            }),
            jsx(AdvancedTablePages, {
                ...table,
                ...paginationControls,
                ...locale
            })
        ]
    });
};
AdvancedTable.displayName = 'AdvancedTable';

export { AdvancedTable, AdvancedTable as default };
//# sourceMappingURL=AdvancedTable.es.js.map
